import { V } from "../misc/Vector.js";
import { Waypoint } from "../router/Waypoint.js";
export { Meander };
class Meander {
    constructor({ startPoint, endPoint, vector, height, arms, regularRadius, epsilon }) {
        this.startPoint = startPoint;
        this.endPoint = endPoint;
        this.vector = vector;
        this.height = height;
        this.arms = arms;
        this.regularRadius = regularRadius;
        this.epsilon = epsilon;
        this.leftVector = V.leftOrthogonal(this.vector);
    }
    getFlipped() {
        const cloned = this.clone();
        cloned.arms = cloned.arms.map(arm => {
            return {
                sign: -arm.sign,
                length: arm.length,
                position: arm.position
            };
        });
        return cloned;
    }
    getWithoutLastArm() {
        const cloned = this.clone();
        if (cloned.arms.length <= 1) {
            return cloned;
        }
        const adjustedArmHeight = Meander.getArmHeight({
            height: this.height,
            regularRadius: this.regularRadius,
            numberOfArms: cloned.arms.length - 1,
            epsilon: this.epsilon
        });
        const adjustedArmHeights = Meander.getArmHeights({
            regularRadius: this.regularRadius,
            numberOfArms: cloned.arms.length - 1,
            armHeight: adjustedArmHeight,
            epsilon: this.epsilon
        });
        cloned.arms = cloned.arms.slice(0, -1);
        adjustedArmHeights.forEach((h, i) => cloned.arms[i].position = h);
        return cloned;
    }
    getWaypoints() {
        const waypoints = [];
        const startWaypoint = this.getStartWaypoint();
        if (startWaypoint !== undefined) {
            waypoints.push(startWaypoint);
        }
        for (let i = 0; i < this.arms.length; i++) {
            const armWaypoint = this.getArmWaypoint(i);
            if (armWaypoint !== undefined) {
                waypoints.push(armWaypoint);
            }
        }
        const endWaypoint = this.getEndWaypoint();
        if (endWaypoint !== undefined) {
            waypoints.push(endWaypoint);
        }
        return waypoints.filter(w => w !== undefined);
    }
    getStartWaypoint() {
        if (this.arms[0].length === undefined) {
            return undefined;
        }
        const position = V.add(V.add(this.startPoint, V.toLength(this.leftVector, -this.arms[0].sign * this.regularRadius)), V.toLength(this.vector, this.regularRadius));
        return new Waypoint({
            position,
            minDistance: 0,
            innerSpacingFactor: 0.5,
            rotation: -this.arms[0].sign,
            trajectories: []
        });
    }
    getArmWaypoint(index) {
        if (index < 0 || index >= this.arms.length) {
            throw new Error('Invalid arm');
        }
        let position;
        const armLength = this.arms[index].length;
        const armSign = this.arms[index].sign;
        if (armLength !== undefined) {
            position = V.add(V.add(this.startPoint, V.toLength(this.leftVector, -armSign * armLength)), V.toLength(this.vector, this.arms[index].position));
        }
        else {
            const predecessorExists = index !== 0;
            const predecessorLength = predecessorExists ? this.arms[index - 1].length : undefined;
            const predecessorMissing = predecessorExists && predecessorLength === undefined;
            const predecessorSign = predecessorExists ? this.arms[index - 1].sign : undefined;
            const successorExists = index !== this.arms.length - 1;
            const successorLength = successorExists ? this.arms[index + 1].length : undefined;
            const successorMissing = successorExists && successorLength === undefined;
            const successorSign = successorExists ? this.arms[index + 1].sign : undefined;
            if (successorMissing && predecessorMissing) {
                return undefined;
            }
            let left = true;
            if (predecessorMissing) {
                if (!successorExists || successorSign === undefined) {
                    return undefined;
                }
                left = successorSign > 0;
            }
            else if (successorMissing) {
                if (!predecessorExists || predecessorSign === undefined) {
                    return undefined;
                }
                left = predecessorSign > 0;
            }
            else {
                if (successorExists && successorSign !== undefined) {
                    left = successorSign > 0;
                }
                else if (predecessorExists && predecessorSign !== undefined) {
                    left = predecessorSign > 0;
                }
                else {
                    return undefined;
                }
            }
            position = V.add(V.add(this.startPoint, V.toLength(this.leftVector, left ? -this.regularRadius : this.regularRadius)), V.toLength(this.vector, this.arms[index].position));
        }
        return new Waypoint({
            position,
            minDistance: 0,
            innerSpacingFactor: 0.5,
            rotation: armSign,
            trajectories: []
        });
    }
    getEndWaypoint() {
        const lastArmLength = this.arms[this.arms.length - 1].length;
        if (lastArmLength === undefined) {
            return undefined;
        }
        const position = V.add(V.add(this.startPoint, V.toLength(this.leftVector, -this.arms[this.arms.length - 1].sign * this.regularRadius)), V.toLength(this.vector, this.height - this.regularRadius));
        return new Waypoint({
            position,
            minDistance: 0,
            innerSpacingFactor: 0.5,
            rotation: -this.arms[this.arms.length - 1].sign,
            trajectories: []
        });
    }
    clone() {
        return new Meander({
            startPoint: this.startPoint,
            endPoint: this.endPoint,
            vector: this.vector,
            height: this.height,
            arms: this.arms.map(a => { return Object.assign({}, a); }),
            regularRadius: this.regularRadius,
            epsilon: this.epsilon
        });
    }
    static createMeander({ startPoint, endPoint, width, spacing, minBendradius, epsilon = 1e-6, armLengthCallBack }) {
        const vector = V.subtract(endPoint, startPoint);
        const height = V.norm(vector);
        const regularRadius = Meander.regularRadius({ width, spacing, minBendradius });
        const entranceHeight = 2 * regularRadius;
        const remainingHeight = height - 2 * entranceHeight - epsilon;
        if (remainingHeight <= 0) {
            return undefined;
        }
        const numberOfArms = Math.floor(remainingHeight / (2 * regularRadius + epsilon));
        if (numberOfArms <= 0) {
            return undefined;
        }
        const armHeight = Meander.getArmHeight({
            height,
            regularRadius,
            numberOfArms,
            epsilon
        });
        const arms = [];
        const armHeights = Meander.getArmHeights({
            regularRadius,
            numberOfArms,
            armHeight,
            epsilon
        });
        for (let i = 0; i < numberOfArms; i++) {
            const currentArmHeight = armHeights[i];
            const armLength = armLengthCallBack();
            const arm = {
                sign: i % 2 === 0 ? -1 : 1,
                length: armLength,
                position: currentArmHeight
            };
            arms.push(arm);
        }
        return new Meander({
            startPoint,
            endPoint,
            vector,
            height,
            arms,
            regularRadius,
            epsilon
        });
    }
    static getArmHeight({ height, regularRadius, numberOfArms, epsilon }) {
        const entranceHeight = 2 * regularRadius;
        const remainingHeight = height - 2 * entranceHeight - epsilon;
        return (remainingHeight / numberOfArms) - epsilon;
    }
    static getArmHeights({ regularRadius, numberOfArms, armHeight, epsilon }) {
        const hs = [];
        let h = 2 * regularRadius + 0.5 * armHeight + epsilon;
        for (let i = 0; i < numberOfArms; i++) {
            hs.push(h);
            h += armHeight + epsilon;
        }
        return hs;
    }
    static regularRadius({ width, spacing, minBendradius }) {
        const entranceSpacing = 0.5 * (width + spacing);
        const entranceBendRadius = minBendradius;
        return entranceBendRadius > entranceSpacing ? entranceBendRadius : entranceSpacing;
    }
}
//# sourceMappingURL=Meander.js.map